import { useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { FileUpload as FileUploadIcon } from '@mui/icons-material';

import {
  CustomButton,
  CustomTextArea,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
} from '../../../../common/components';
import { useImportSupplierOpeningBalances } from '../../../../hooks/useSupplierLedger';
import { parseNumber } from '../../utils/supplierLedgerFormatters';

const ImportOpeningBalancesDrawer = ({ open, onClose }) => {
  const [rawText, setRawText] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const importOpeningBalances = useImportSupplierOpeningBalances();

  useEffect(() => {
    if (!open) return;
    setRawText('');
    setResult(null);
    setError('');
  }, [open]);

  const parseRows = () => rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line, index) => !(index === 0 && /proveedor|cuit|fecha|monto/i.test(line)))
    .map((line) => {
      const [provider, adjustmentDate, amount, currency = 'ARS', exchangeRate = '', concept = 'Saldo inicial'] = line.split(';').map((part) => part.trim());
      return {
        provider,
        cuit: /^\d/.test(provider || '') ? provider : undefined,
        adjustment_date: adjustmentDate,
        amount: parseNumber(amount),
        currency: currency || 'ARS',
        exchange_rate_to_ars: currency === 'USD' ? parseNumber(exchangeRate) : null,
        concept: concept || 'Saldo inicial',
      };
    });

  const handleSubmit = async () => {
    setError('');
    setResult(null);
    const rows = parseRows();
    if (!rows.length) {
      setError('Pegá al menos una fila para importar.');
      return;
    }
    try {
      const response = await importOpeningBalances.mutateAsync(rows);
      setResult(response);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    }
  };

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title="Importar saldos iniciales"
      width={{ xs: '100vw', sm: 760 }}
      footer={(
        <DrawerFormActions
          summary={<DrawerFormActionSummary label="Filas detectadas" value={parseRows().length} />}
        >
          <CustomButton variant="outlined" onClick={onClose}>Cerrar</CustomButton>
          <CustomButton variant="base" startIcon={<FileUploadIcon />} onClick={handleSubmit} isLoading={importOpeningBalances.isPending}>
            Importar
          </CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {error && <Box sx={{ mx: 2 }}><Alert severity="error">{error}</Alert></Box>}
        {result && (
          <Box sx={{ mx: 2 }}>
            <Alert severity={result.errors?.length ? 'warning' : 'success'}>
              Importados: {result.created?.length || 0}. Errores: {result.errors?.length || 0}.
            </Alert>
          </Box>
        )}
        <DrawerFormSection title="CSV de saldos" grid={false}>
          <CustomTextArea
            label="Filas"
            name="opening_balances"
            value={rawText}
            onChange={(event) => setRawText(event.target.value)}
            minRows={10}
            placeholder="Proveedor o CUIT;2026-10-01;150000;ARS;;Saldo inicial"
            sx={{ mt: 0 }}
          />
          {result?.errors?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {result.errors.slice(0, 8).map((row) => (
                <Typography key={row.row} variant="caption" sx={{ display: 'block', color: 'error.main', fontWeight: 700 }}>
                  Fila {row.row}: {row.error}
                </Typography>
              ))}
            </Box>
          )}
        </DrawerFormSection>
      </Box>
    </DrawerForm>
  );
};

export default ImportOpeningBalancesDrawer;
